import React, { useState } from 'react';
import { Chip, IconButton, Tooltip } from '@lumx/react';
import MdiIcon from '@mdi/react';
import { mdiCached, mdiChevronDown, mdiChevronRight, mdiApplicationOutline, mdiAccountMultipleOutline } from '@mdi/js';
import { MobileConfig, OUNode, TreeNode } from '../../types';
import { Icon } from '../shared/icons';
import MobileConfigPicker from '../shared/MobileConfigPicker';
import EditableName from './EditableName';
import RowOverflowMenu from '../shared/RowOverflowMenu';

interface Props {
  node: TreeNode;
  depth: number;
  expanded: boolean;
  mobileConfigs: MobileConfig[];
  allNodes: OUNode[];
  siteCount: number;
  siteNames: string[];
  sitesExpanded: boolean;
  onSitesToggle: (id: string) => void;
  onToggle: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onConvertType: (id: string) => void;
  onAddChild: (parentId: string | null) => void;
  onDeleteRequest: (id: string) => void;
  onMobileConfigChange: (nodeId: string, configId: string | null, conflict: { configId: string; fromNodeName: string } | null) => void;
  onViewUsers: (id: string) => void;
  showSitesColumn?: boolean;
  readOnlyAssignments?: boolean;
  readOnlyStructure?: boolean;
  onDragStart: (e: React.DragEvent, kind: 'ou', id: string) => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onDrop: (e: React.DragEvent, targetNodeId: string) => void;
  onDragLeave: (e: React.DragEvent) => void;
  dragOverId: string | null;
  draggingId: string | null;
  dragInsert: { targetId: string; position: 'before' | 'after' } | null;
}

const OUTreeRow: React.FC<Props> = ({
  node, depth, expanded, mobileConfigs, allNodes, siteCount,
  sitesExpanded, onSitesToggle, onViewUsers,
  onToggle, onRename, onConvertType, onAddChild, onDeleteRequest,
  onMobileConfigChange, onDragStart, onDragOver, onDrop, onDragLeave,
  dragOverId, draggingId, dragInsert, siteNames,
  showSitesColumn = false, readOnlyAssignments = false, readOnlyStructure = false,
}) => {
  const hasChildren = node.children.length > 0;
  const isDragOver = dragOverId === node.id;
  const isDragging = draggingId === node.id;
  const isDragInsertBefore = dragInsert?.targetId === node.id && dragInsert.position === 'before';
  const isDragInsertAfter = dragInsert?.targetId === node.id && dragInsert.position === 'after';
  const [renameCount, setRenameCount] = useState(0);

  return (
    <div
      className={`tree-row ou-row ${expanded ? 'expanded' : ''} ${isDragOver ? 'drag-over' : ''} ${isDragging ? 'dragging' : ''} ${isDragInsertBefore ? 'drag-insert-before' : ''} ${isDragInsertAfter ? 'drag-insert-after' : ''}`}
      draggable={!readOnlyStructure}
      onDragStart={readOnlyStructure ? undefined : e => onDragStart(e, 'ou', node.id)}
      onDragOver={e => onDragOver(e, node.id)}
      onDrop={e => onDrop(e, node.id)}
      onDragLeave={onDragLeave}
    >
      <div className="row-controls">
        {!readOnlyStructure && (
          <span className="drag-handle" title="Drag to reparent" onMouseDown={e => e.stopPropagation()}>
            {Icon.drag}
          </span>
        )}
        <button
          className={`expand-btn ${expanded ? 'expanded' : ''}`}
          style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          onClick={() => onToggle(node.id)}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          {Icon.chevronRight}
        </button>
      </div>

      <div className="name-cell" style={{ paddingLeft: `calc(var(--space-2) + ${(depth - 1) * 20}px)` }}>
        <span className="name-icon">{node.type === 'Location' ? Icon.location : Icon.building}</span>
        {readOnlyStructure
          ? <span className="editable-name">{node.name}</span>
          : <EditableName value={node.name} onSave={name => onRename(node.id, name)} forceEditCount={renameCount} />
        }
        {!readOnlyStructure && node.type === 'OrganizationalUnit' && (
          <button
            className="add-unit-btn"
            onClick={e => { e.stopPropagation(); onAddChild(node.id); }}
            title="Add child unit"
          >
            {Icon.plus} Add unit
          </button>
        )}
        {!showSitesColumn && siteCount > 0 && (
          <Chip
            size="s"
            isClickable
            before={<span style={{ display: 'flex', alignItems: 'center' }}><MdiIcon path={mdiApplicationOutline} size={0.55} /></span>}
            after={<span style={{ display: 'flex', alignItems: 'center' }}><MdiIcon path={sitesExpanded ? mdiChevronDown : mdiChevronRight} size={0.65} /></span>}
            onClick={e => { (e as React.MouseEvent).stopPropagation(); onSitesToggle(node.id); }}
          >
            {siteCount} site{siteCount > 1 ? 's' : ''}
          </Chip>
        )}
      </div>

      {readOnlyAssignments ? (
        <div className="mobile-config-cell sites-col-text">
          {node.mobileConfigId
            ? mobileConfigs.find(c => c.id === node.mobileConfigId)?.name ?? '—'
            : '—'}
        </div>
      ) : (
        <div className="mobile-config-cell">
          <MobileConfigPicker
            value={node.mobileConfigId}
            configs={mobileConfigs}
            nodes={allNodes}
            nodeId={node.id}
            onChange={(configId, conflict) => onMobileConfigChange(node.id, configId, conflict)}
          />
        </div>
      )}

      {showSitesColumn && (
        <div className="sites-col-text" style={{ padding: '0 var(--space-2)' }}>
          {siteCount > 0 ? (
            <Chip
              size="s"
              isClickable
              before={<span style={{ display: 'flex', alignItems: 'center' }}><MdiIcon path={mdiApplicationOutline} size={0.55} /></span>}
              after={<span style={{ display: 'flex', alignItems: 'center' }}><MdiIcon path={sitesExpanded ? mdiChevronDown : mdiChevronRight} size={0.65} /></span>}
              onClick={e => { (e as React.MouseEvent).stopPropagation(); onSitesToggle(node.id); }}
            >
              {siteCount} site{siteCount > 1 ? 's' : ''}
            </Chip>
          ) : '—'}
        </div>
      )}

      <div className="row-actions-cell">
        <IconButton
          label="Show groups"
          icon={mdiAccountMultipleOutline}
          emphasis="low"
          size="s"
          onClick={e => { (e as React.MouseEvent).stopPropagation(); onViewUsers(node.id); }}
        />
        {!readOnlyStructure && (
          <>
            <Tooltip label={node.type === 'Location' ? 'Convert to Org unit' : 'Convert to Location'}>
              <IconButton
                label={node.type === 'Location' ? 'Convert to Org unit' : 'Convert to Location'}
                icon={mdiCached}
                emphasis="low"
                size="s"
                onClick={e => { (e as React.MouseEvent).stopPropagation(); onConvertType(node.id); }}
              />
            </Tooltip>
            <RowOverflowMenu
              onRename={() => setRenameCount(c => c + 1)}
              onDelete={() => onDeleteRequest(node.id)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default OUTreeRow;
